import React from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Visibility } from "@mui/icons-material";
import { VisibilityOff } from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";

import useRegister from "../../hooks/useRegister";
import { useTokens } from "../../theme";

export default function RegisterForm() {
  const registerProps = useRegister();
  const tokens = useTokens();
  const [validationError, setValidationError] = React.useState(false);
  const [touched, setTouched] = React.useState({});
  const { showPassword, handlers, loading, registerData, formData } = registerProps;
  const { handleFormChange } = handlers;

  const FIELD_LABELS = {
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    password: "Password",
    confirmPassword: "Password confirmation",
  };
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailInvalid = formData.email !== "" && !EMAIL_PATTERN.test(formData.email);

  // Shown only once a field has been visited, so a pristine form isn't a wall of red.
  const fieldMessage = (name) => {
    if (!touched[name]) return null;
    if (formData[name] === "") return `${FIELD_LABELS[name] || "This field"} is required`;
    if (name === "email" && emailInvalid) return "Enter a valid email address";
    if (name === "confirmPassword" && formData.confirmPassword !== formData.password) {
      return "Passwords do not match";
    }
    return null;
  };

  const requiredFieldsNotFilled = () => {
    let result = false;

    for (const value of Object.values(formData)) {
      if (value === "" || value == null) {
        result = true;
        break;
      }
    }

    return result;
  };

  React.useEffect(() => {
    let hasError = requiredFieldsNotFilled() || emailInvalid;

    setValidationError(hasError);
  }, [
    formData.firstName,
    formData.lastName,
    formData.email,
    formData.effectiveDate,
    formData.email,
    formData.password,
    formData.confirmPassword,
  ]);

  const fields = [
    { name: "firstName", id: "register-form-fname", label: "First Name", autoComplete: "given-name", icon: <PersonOutlineIcon fontSize="small" /> },
    { name: "lastName", id: "register-form-lname", label: "Last Name", autoComplete: "family-name", icon: <PersonOutlineIcon fontSize="small" /> },
    { name: "email", id: "register-form-email", label: "Email", type: "email", autoComplete: "email", icon: <MailOutlineIcon fontSize="small" /> },
    { name: "password", id: "register-form-password", label: "Password", secret: true, toggle: true, hint: "Use at least 8 characters.", autoComplete: "new-password", icon: <LockOutlinedIcon fontSize="small" /> },
    { name: "confirmPassword", id: "register-form-confirm-password", label: "Confirm Password", secret: true, autoComplete: "new-password", icon: <LockOutlinedIcon fontSize="small" /> },
  ];

  return (
    <Box component="div">
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography
          component="h2"
          sx={{ fontSize: "1.25rem", fontWeight: 600, color: tokens.foreground }}
        >
          Create an account
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: tokens.mutedForeground }}>
          All fields are required.
        </Typography>
      </Stack>
      <Stack spacing={2}>
        {fields.map((field) => {
          const message = fieldMessage(field.name);
          return (
            <TextField
              key={field.name}
              id={field.id}
              label={field.label}
              variant="outlined"
              fullWidth
              autoComplete={field.autoComplete}
              inputProps={{ "aria-required": true }}
              type={field.secret ? (showPassword ? "text" : "password") : field.type || "text"}
              error={Boolean(message)}
              helperText={message || field.hint}
              onBlur={() => setTouched((prev) => ({ ...prev, [field.name]: true }))}
              onChange={(event) => handleFormChange(field.name, event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{field.icon}</InputAdornment>
                ),
                endAdornment: field.toggle ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      aria-pressed={showPassword}
                      onClick={handlers.toggle}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          );
        })}
      </Stack>
      <Stack spacing={1.5} sx={{ mt: 3 }}>
        <LoadingButton
          loading={loading}
          variant="contained"
          size="large"
          fullWidth
          startIcon={<AppRegistrationIcon />}
          onClick={registerData}
          disabled={validationError}
        >
          Register
        </LoadingButton>
        <Button variant="outlined" size="large" fullWidth onClick={handlers.return}>
          Sign In With Existing Account?
        </Button>
      </Stack>
    </Box>
  );
}
